import React from 'react';
import {
  Dialog,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import docStyles from './docs.module.css';
import {
  CustomDialogTitle,
  CustomDialogContent,
  CustomDialogAction,
  CustomButton,
  StyledCard,
} from 'custom/components';
import { AddIcon, CloseIconBtn, SaveTickIcon, CancelIcon } from 'custom/constants';
import { dateTolocale } from 'utils/converter';
import { grey } from '@mui/material/colors';

const DocStackModal = (props) => {
  const {
    stackDocs = [],
    modal,
    closeModal,
    uploadError,
    retryUpload,
    uploadingDoc,
    selected,
    handleRadioChange,
    handleOpen,
    handleDeleteDoc,
    navigateToStack,
  } = props;

  // ✅ Sort: pending docs first, then newest first
  const sortedStackDocs = stackDocs.length
    ? [...stackDocs].sort((a, b) => {
      const aPending = a.scan_status === 'PENDING';
      const bPending = b.scan_status === 'PENDING';
      if (aPending && !bPending) return -1;
      if (!aPending && bPending) return 1;
      return new Date(b.created) - new Date(a.created);
    })
    : [];

  return (
    <Dialog
      aria-labelledby='form-dialog-title'
      open={modal === 'docStack'}
      onClose={closeModal}
      maxWidth='md'
      fullWidth
    >
      <CustomDialogTitle id='form-dialog-title'>
        Choose Document
        <CloseIconBtn onClick={closeModal} />
      </CustomDialogTitle>

      <CustomDialogContent>
        {uploadError && (
          <Stack
            direction='row'
            spacing={2}
            alignItems='center'
            className={docStyles.errorBox}
          >
            <Typography color='error'>{uploadError}</Typography>
            <CustomButton
              variant='outlined'
              onClick={retryUpload}
              loading={uploadingDoc}
              disabled={uploadingDoc}
            >
              Retry
            </CustomButton>
          </Stack>
        )}

        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          borderBottom={'1px solid #e8e8e8'}
        >
          <Typography variant='body2' color='primary' mb={2}>
            Select a document from the list to view
          </Typography>
          <Stack direction='row' alignItems='center' spacing={2}>
            <Typography variant='body2' color='primary' mb={2}>
              Upload a new document
            </Typography>
            <AddIcon title='Add' onClick={() => handleOpen('addDoc')} />
          </Stack>
        </Stack>

        <StyledCard
          sx={{
            mt: 2,
            px: 4,
            py: 4,
          }}
        >
          <RadioGroup
            aria-labelledby='doc-stack-radio-button'
            name='doc_stack'
            value={selected}
            onChange={handleRadioChange}
            row
          >
            <Grid container spacing={3}>
              {sortedStackDocs.map((doc) => {
                const checked = String(doc.id) === String(selected);
                const isProcessing = doc.scan_status === 'PENDING';
                const isFailed = doc.scan_status === 'FAILED';

                return (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    key={doc.id}
                    style={{ position: 'relative' }}
                  >
                    <FormControlLabel
                      value={String(doc.id)}
                      control={
                        <Radio
                          icon={<></>}
                          checkedIcon={<SaveTickIcon className={docStyles.stackCheckedIcon} />}
                          classes={{
                            root: docStyles.stackRoot,
                            checked: docStyles.stackChecked,
                          }}
                          disabled={isProcessing}
                        />
                      }
                      label={
                        <Stack
                          direction='row'
                          alignItems='center'
                          justifyContent='space-between'
                        >
                          <Stack>
                            <Typography
                              variant='inherit'
                              color={checked ? 'white' : 'text.primary'}
                              fontWeight={checked ? 'bold' : 'normal'}
                              lineHeight='normal'
                            >
                              {doc.name}
                            </Typography>
                            <Typography
                              variant='caption'
                              color={isFailed ? 'error' : checked ? grey[300] : 'text.secondary'}
                              fontWeight={checked ? 'bold' : 'normal'}
                              lineHeight='normal'
                            >
                              {isProcessing
                                ? 'Processing...'
                                : isFailed
                                  ? 'Failed'
                                  : doc.created
                                    ? dateTolocale(doc.created, 'DDMMMYY HH:mm').toUpperCase()
                                    : ''}
                            </Typography>
                          </Stack>
                        </Stack>
                      }
                      classes={{
                        root: `${docStyles.stackLabelRoot} ${checked ? docStyles.stackLabelRootChecked : ''}`,
                        label: `${docStyles.stackLabel}`,
                      }}
                    />
                    <CancelIcon
                      className={docStyles.cancelIcon}
                      color='error'
                      style={{ cursor: 'pointer' }}
                      title='Delete document'
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDoc?.(doc.id, e.currentTarget);
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </RadioGroup>
        </StyledCard>
      </CustomDialogContent>

      <CustomDialogAction>
        <CustomButton variant='contained' onClick={navigateToStack}>
          Open AWB Doc Stack
        </CustomButton>
      </CustomDialogAction>
    </Dialog>
  );
};

export default DocStackModal;
